import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Play, CheckCircle, Clock, Users, Award, FileText, Target, Star, GraduationCap, Heart, Baby, Home, Sparkles, Eye, Smile, HandHeart, TreePine } from 'lucide-react';

// Couleurs pour le TP IEPE - thème petite enfance / nature
const theme = {
  primary: '#059669',      // Vert émeraude
  secondary: '#F59E0B',    // Orange chaleureux
  accent1: '#10B981',      // Vert clair (BC1)
  accent2: '#6366F1',      // Indigo (BC2)
  accent3: '#EC4899',      // Rose (BC3)
  background: '#ECFDF5',   // Fond vert très clair
  text: '#1E293B',
  success: '#10B981',
  error: '#EF4444',
};

const slides = [
  // INTRODUCTION
  {
    id: 1,
    title: "Réussir sa VAE TP IEPE",
    subtitle: "Titre Professionnel Intervenant en Enfance et Petite Enfance",
    description: "Les étapes clés pour réussir votre validation",
    type: "title",
  },
  {
    id: 2,
    title: "Pourquoi choisir la VAE TP IEPE ?",
    type: "benefits",
    items: [
      "Valoriser votre expérience auprès des jeunes enfants",
      "Obtenir un titre professionnel de niveau 4 reconnu par l'État",
      "Travailler en crèche, halte-garderie ou multi-accueil",
      "Accompagner le développement des enfants de 0 à 6 ans",
      "Évoluer vers des postes d'encadrement"
    ]
  },
  {
    id: 3,
    title: "Conditions d'éligibilité",
    type: "eligibility",
    items: [
      "1 an d'expérience conseillé auprès des jeunes enfants",
      "Activités en lien avec l'accueil et l'accompagnement de jeunes enfants",
      "Expérience en structure collective (crèche, halte-garderie, multi-accueil)",
      "Travail auprès d'enfants de 0 à 6 ans"
    ]
  },
  {
    id: 4,
    title: "Les étapes de la certification TP IEPE",
    type: "steps",
    items: [
      { num: 1, text: "Déposer sa demande de faisabilité" },
      { num: 2, text: "Rédiger le livret de certification" },
      { num: 3, text: "Déposer le livret de certification" },
      { num: 4, text: "Préparer le diaporama" },
      { num: 5, text: "Passer les épreuves d'évaluation" },
      { num: 6, text: "Décision du jury" }
    ]
  },
  {
    id: 5,
    title: "Validation par blocs",
    type: "info-bloc",
    text: "Chaque bloc de compétences constitue une partie de votre diplôme qui peut être validée indépendamment des autres et reste acquise à vie."
  },
  {
    id: 6,
    title: "Les 3 blocs de compétences",
    type: "blocs-overview",
    blocs: [
      { num: 1, title: "Contribuer à l'accueil et répondre aux besoins fondamentaux", color: theme.accent1 },
      { num: 2, title: "Accompagner le développement des jeunes enfants", color: theme.accent2 },
      { num: 3, title: "Accompagner les parents et l'entourage", color: theme.accent3 }
    ]
  },

  // BLOC 1 - Accueil et besoins fondamentaux
  {
    id: 7,
    title: "Bloc 1 – Contribuer à l'accueil",
    subtitle: "des jeunes enfants et à la réponse à leurs besoins fondamentaux",
    type: "bloc-title",
    color: theme.accent1,
    icon: "baby",
    code: "RNCP41849BC01"
  },
  {
    id: 8,
    title: "BC1 – Compétences attendues",
    type: "competence",
    color: theme.accent1,
    items: [
      "Participer à l'observation quotidienne des besoins des jeunes enfants en tenant compte de leur singularité et de leurs particularités",
      "Assurer le bien-être des jeunes enfants en répondant à leurs besoins physiologiques fondamentaux",
      "Assurer le bien-être des jeunes enfants en répondant à leurs besoins affectifs et relationnels",
      "Participer à l'élaboration, la mise en œuvre et l'évaluation du projet éducatif"
    ]
  },

  // BLOC 2 - Développement des jeunes enfants
  {
    id: 9,
    title: "Bloc 2 – Accompagner le développement",
    subtitle: "des jeunes enfants",
    type: "bloc-title",
    color: theme.accent2,
    icon: "sparkles",
    code: "RNCP41849BC02"
  },
  {
    id: 10,
    title: "BC2 – Compétences attendues",
    type: "competence",
    color: theme.accent2,
    items: [
      "Aménager un espace d'accueil adapté aux jeunes enfants et au contexte en organisant les activités au quotidien",
      "Élaborer et mettre en place une activité d'éveil et d'autonomie, individuelle ou de groupe",
      "Mettre en œuvre des actions éducatives hors les murs favorisant l'ouverture sur la culture et sur une démarche environnementale",
      "Contribuer à l'élaboration, à la mise en œuvre et à l'ajustement des projets d'accueil personnalisé des jeunes enfants en tenant compte de leur singularité et de leurs particularités"
    ]
  },

  // BLOC 3 - Accompagner les parents
  {
    id: 11,
    title: "Bloc 3 – Accompagner les parents",
    subtitle: "ou l'entourage des jeunes enfants",
    type: "bloc-title",
    color: theme.accent3,
    icon: "heart",
    code: "RNCP41849BC03"
  },
  {
    id: 12,
    title: "BC3 – Compétences attendues",
    type: "competence",
    color: theme.accent3,
    items: [
      "Accueillir au quotidien les parents, l'entourage et construire une alliance éducative",
      "Participer à l'élaboration et à la mise en œuvre des actions d'accompagnement à la parentalité",
      "Participer à l'élaboration et à la mise en œuvre des actions de prévention précoce et inclusive en direction des jeunes enfants et en soutien à la fonction parentale"
    ]
  },

  // CONSEILS ET ORAL
  {
    id: 13,
    title: "Conseils pour réussir",
    type: "tips",
    tips: [
      "Décrire des situations d'accueil concrètes",
      "Analyser vos pratiques auprès des enfants",
      "Justifier vos choix pédagogiques",
      "Montrer votre posture bienveillante"
    ],
    example: {
      title: "Exemple de formulation attendue",
      wrong: "« Je m'occupe des enfants à la crèche »",
      right: "« J'accueille Emma, 18 mois, et sa maman. J'observe qu'Emma est fatiguée ce matin. Je transmets cette information à l'équipe et j'adapte les activités en lui proposant un temps calme avec des livres. »"
    }
  },
  {
    id: 14,
    title: "Les épreuves d'évaluation",
    type: "exam-phases",
    phases: [
      {
        num: 1,
        title: "Mise en Situation Professionnelle",
        duration: "1h10",
        color: "#059669",
        parts: [
          { label: "La Pratique", duration: "20 min", desc: "Change et couchage d'un poupon d'entraînement (moins de 6 mois). Le jury observe mais n'intervient pas." },
          { label: "L'Étude de Cas", duration: "50 min", desc: "Tirage au sort d'un sujet. Préparation écrite (analyse de 4 situations). Le brouillon servira de support pour l'oral." }
        ]
      },
      {
        num: 2,
        title: "Entretien Technique",
        duration: "1h35",
        color: "#D97706",
        parts: [
          { label: "Debriefing", duration: "10 min", desc: "Questions du jury sur votre pratique et vos observations." },
          { label: "Votre Présentation", duration: "25 min", desc: "Vous présentez à l'oral l'analyse de votre étude de cas." },
          { label: "Échange sur le Cas", duration: "30 min", desc: "Questions/Réponses avec le jury sur les situations présentées." },
          { label: "Compétences Complémentaires", duration: "30 min", desc: "Questions sur le Projet Éducatif, l'aménagement et les actions hors les murs." }
        ]
      },
      {
        num: 3,
        title: "Questionnement à partir de productions",
        duration: "1h00",
        color: "#2563EB",
        parts: [
          { label: "Présentation orale", duration: "30 min", desc: "Monologue — Soutenance orale à partir du diaporama réalisé en amont." },
          { label: "Entretien avec le jury", duration: "30 min", desc: "Dialogue — Questions/réponses sur votre diaporama et vos expériences." }
        ],
        diaporama: {
          title: "Structure du diaporama",
          sections: [
            { label: "Partie 1 — Observation", slides: "5-7 slides", desc: "Présentation de la structure et méthodologie d'observation des besoins de l'enfant" },
            { label: "Partie 2 — Activités", slides: "5-7 slides", desc: "1 activité individuelle + 1 activité de groupe : thème, méthode, évaluation" },
            { label: "Partie 3 — Parentalité", slides: "5-7 slides", desc: "Actions d'accompagnement à la parentalité, démarches et partenariats" }
          ],
          total: "15 à 21 diapositives"
        }
      },
      {
        num: 4,
        title: "Entretien Final",
        duration: "15 min",
        color: "#8B5CF6",
        parts: [
          { label: "Échange global", duration: "15 min", desc: "Échange sur le Dossier Professionnel (DP). Évaluation de votre compréhension du travail d'équipe, connaissance du secteur Petite Enfance et culture professionnelle." }
        ]
      }
    ]
  },
  {
    id: 15,
    title: "Décision du jury",
    type: "decision",
    decisions: [
      { status: "success", text: "Validation totale", desc: "Vous obtenez votre TP IEPE !" },
      { status: "partial", text: "Validation partielle", desc: "Blocs de compétences acquis à vie, reste à valider les autres" },
      { status: "retry", text: "Ajournement", desc: "Possibilité de représenter le dossier après travail complémentaire" }
    ]
  },
  {
    id: 16,
    title: "Débouchés professionnels",
    type: "careers",
    jobs: [
      "Agent de crèche",
      "Auxiliaire petite enfance",
      "Animateur(trice) petite enfance",
      "Agent en halte-garderie",
      "Agent en multi-accueil"
    ]
  },
  {
    id: 17,
    title: "En résumé",
    type: "summary",
    points: [
      "Le TP IEPE valorise vos compétences auprès des jeunes enfants",
      "Chaque bloc validé est acquis à vie",
      "Une préparation méthodique favorise la réussite"
    ]
  }
];

const getIcon = (iconName, size = 50) => {
  switch (iconName) {
    case 'baby': return <Baby size={size} />;
    case 'sparkles': return <Sparkles size={size} />;
    case 'heart': return <Heart size={size} />;
    default: return <Award size={size} />;
  }
};

const SlideContent = ({ slide }) => {
  switch (slide.type) {
    case "title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent1} 100%)`,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '100px', height: '100px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 2rem', border: '3px solid rgba(255,255,255,0.3)'
          }}>
            <Baby size={50} style={{ color: 'white' }} />
          </div>
          <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.75rem', color: 'white' }}>
            {slide.title}
          </h2>
          <p style={{ fontSize: '1rem', marginBottom: '0.5rem', opacity: 0.95 }}>{slide.subtitle}</p>
          <p style={{ fontSize: '1rem', opacity: 0.85 }}>{slide.description}</p>
        </div>
      );

    case "benefits":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '700px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.secondary}`
              }}>
                <Star size={20} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "eligibility":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.success}`
              }}>
                <CheckCircle size={22} style={{ color: theme.success, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "steps":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', maxWidth: '750px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center'
              }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%', background: theme.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem',
                  color: 'white', fontWeight: 'bold', fontSize: '1.1rem'
                }}>{item.num}</div>
                <span style={{ fontSize: '0.85rem', color: theme.text }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "info-bloc":
      return (
        <div style={{ padding: '2rem' }}>
          <div style={{
            background: `linear-gradient(135deg, ${theme.accent1}15 0%, ${theme.accent2}15 100%)`,
            borderRadius: '1rem',
            padding: '2rem',
            textAlign: 'center',
            border: `2px solid ${theme.primary}30`
          }}>
            <Sparkles size={50} style={{ color: theme.primary, marginBottom: '1rem' }} />
            <p style={{ fontSize: '1.2rem', color: theme.text, lineHeight: '1.8', maxWidth: '600px', margin: '0 auto' }}>
              {slide.text}
            </p>
          </div>
        </div>
      );

    case "blocs-overview":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '700px', margin: '0 auto' }}>
            {slide.blocs.map((bloc, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.25rem',
                background: bloc.color, borderRadius: '0.75rem', color: 'white'
              }}>
                <div style={{
                  width: '50px', height: '50px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1.25rem', flexShrink: 0
                }}>BC{bloc.num}</div>
                <h4 style={{ fontWeight: 'bold', fontSize: '1rem' }}>{bloc.title}</h4>
              </div>
            ))}
          </div>
        </div>
      );

    case "bloc-title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: slide.color,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
          }}>
            {getIcon(slide.icon, 40)}
          </div>
          <p style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.5rem' }}>{slide.code}</p>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>{slide.title}</h2>
          <p style={{ fontSize: '1.1rem', opacity: 0.9 }}>{slide.subtitle}</p>
        </div>
      );

    case "competence":
      return (
        <div style={{ padding: '1rem' }}>
          <div style={{ 
            background: slide.color, color: 'white', padding: '1rem 1.5rem', 
            borderRadius: '0.75rem 0.75rem 0 0', marginBottom: '0'
          }}>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 'bold' }}>{slide.title}</h2>
          </div>
          <div style={{ 
            background: 'white', borderRadius: '0 0 0.75rem 0.75rem', 
            padding: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem 0',
                borderBottom: idx < slide.items.length - 1 ? `1px solid #eee` : 'none'
              }}>
                <CheckCircle size={18} style={{ color: slide.color, flexShrink: 0, marginTop: '2px' }} />
                <span style={{ fontSize: '0.9rem', color: theme.text, lineHeight: '1.5' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "tips":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '600px', margin: '0 auto 1rem' }}>
            {slide.tips.map((tip, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem',
                background: 'white', borderRadius: '0.5rem', boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
              }}>
                <Target size={18} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{tip}</span>
              </div>
            ))}
          </div>
          <div style={{
            maxWidth: '650px', margin: '0 auto', padding: '1rem',
            background: '#f8f9fa', borderRadius: '0.75rem', border: `2px solid ${theme.primary}`
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '0.75rem', color: theme.primary, textAlign: 'center', fontSize: '0.95rem' }}>
              {slide.example.title}
            </p>
            <div style={{ padding: '0.5rem 0.75rem', background: '#ffebee', borderRadius: '0.5rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: theme.error, fontWeight: 'bold' }}>✗</span>
              <span style={{ color: theme.error, fontSize: '0.9rem' }}>{slide.example.wrong}</span>
            </div>
            <div style={{ padding: '0.5rem 0.75rem', background: '#e8f5e9', borderRadius: '0.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <span style={{ color: theme.success, fontWeight: 'bold' }}>✓</span>
              <span style={{ color: theme.success, fontSize: '0.8rem' }}>{slide.example.right}</span>
            </div>
          </div>
        </div>
      );

    case "exam-phases":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.25rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '800px', margin: '0 auto' }}>
            {slide.phases.map((phase, idx) => (
              <div key={idx} style={{ background: 'white', borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1px solid ${phase.color}25` }}>
                <div style={{ background: phase.color, padding: '0.5rem 0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white' }}>
                    <span style={{ background: 'rgba(255,255,255,0.25)', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>{phase.num}</span>
                    <strong style={{ fontSize: '0.85rem' }}>{phase.title}</strong>
                  </div>
                  <span style={{ background: 'rgba(255,255,255,0.2)', padding: '0.15rem 0.5rem', borderRadius: '50px', fontSize: '0.7rem', fontWeight: 600, color: 'white' }}>{phase.duration}</span>
                </div>
                <div style={{ padding: '0.5rem 0.75rem' }}>
                  {phase.parts.map((part, pi) => (
                    <div key={pi} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.35rem', alignItems: 'flex-start' }}>
                      <div style={{ minWidth: '6px', height: '6px', borderRadius: '50%', background: phase.color, marginTop: '6px', flexShrink: 0 }} />
                      <div>
                        <strong style={{ fontSize: '0.8rem', color: phase.color }}>{part.label}</strong>
                        <span style={{ fontSize: '0.75rem', color: '#6B7280', marginLeft: '0.35rem' }}>({part.duration})</span>
                        <p style={{ fontSize: '0.75rem', color: '#4B5563', lineHeight: 1.4, marginTop: '0.1rem' }}>{part.desc}</p>
                      </div>
                    </div>
                  ))}
                  {phase.diaporama && (
                    <div style={{ marginTop: '0.35rem', background: `${phase.color}08`, padding: '0.5rem', borderRadius: '0.5rem', border: `1px solid ${phase.color}20` }}>
                      <strong style={{ fontSize: '0.78rem', color: phase.color, display: 'block', marginBottom: '0.25rem' }}>{phase.diaporama.title}</strong>
                      {phase.diaporama.sections.map((s, si) => (
                        <div key={si} style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.2rem', alignItems: 'flex-start' }}>
                          <CheckCircle size={12} style={{ color: phase.color, flexShrink: 0, marginTop: '1px' }} />
                          <span style={{ fontSize: '0.72rem', color: '#374151' }}><strong>{s.label}</strong> ({s.slides}) — {s.desc}</span>
                        </div>
                      ))}
                      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: phase.color, marginTop: '0.25rem', textAlign: 'center' }}>Total : {phase.diaporama.total}</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case "oral":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ maxWidth: '500px', margin: '0 auto', background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            {slide.details.map((detail, idx) => (
              <div key={idx} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem',
                background: theme.background, borderRadius: '0.5rem', marginBottom: '0.75rem'
              }}>
                <span style={{ fontWeight: 'bold', color: theme.text }}>{detail.label}</span>
                <span style={{ color: theme.primary, fontWeight: 'bold' }}>{detail.value}</span>
              </div>
            ))}
            <div style={{ padding: '0.75rem', background: theme.primary + '15', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '0.9rem', color: theme.text, fontStyle: 'italic' }}>{slide.note}</p>
            </div>
          </div>
        </div>
      );

    case "decision":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.decisions.map((decision, idx) => (
              <div key={idx} style={{
                padding: '1rem',
                background: decision.status === 'success' ? '#e8f5e9' : decision.status === 'partial' ? '#fff3e0' : '#ffebee',
                borderRadius: '0.75rem',
                borderLeft: `4px solid ${decision.status === 'success' ? theme.success : decision.status === 'partial' ? '#ff9800' : theme.error}`
              }}>
                <h4 style={{ fontWeight: 'bold', marginBottom: '0.25rem', color: decision.status === 'success' ? theme.success : decision.status === 'partial' ? '#e65100' : theme.error }}>
                  {decision.text}
                </h4>
                <span style={{ fontSize: '0.9rem', color: theme.text }}>{decision.desc}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "careers":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.jobs.map((job, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <Baby size={18} style={{ color: theme.primary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.9rem', color: theme.text }}>{job}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "summary":
      return (
        <div style={{ 
          padding: '2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent1} 100%)`,
          borderRadius: '1rem',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', color: 'white' }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '550px', margin: '0 auto' }}>
            {slide.points.map((point, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '0.75rem',
                background: 'rgba(255,255,255,0.15)', borderRadius: '0.75rem'
              }}>
                <CheckCircle size={20} style={{ color: 'white', flexShrink: 0 }} />
                <span style={{ color: 'white', fontSize: '0.95rem' }}>{point}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(255,255,255,0.9)', borderRadius: '0.75rem' }}>
            <p style={{ color: theme.primary, fontWeight: 'bold' }}>
              Lancez-vous dans l'aventure VAE TP IEPE avec notre accompagnement personnalisé !
            </p>
          </div>
        </div>
      );

    default:
      return null;
  }
};

const TPIEPEPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  React.useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => {
          if (prev === slides.length - 1) { setIsPlaying(false); return prev; }
          return prev + 1;
        });
      }, 6000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  return (
    <div style={{ borderRadius: '1rem', overflow: 'hidden', background: theme.background, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} data-testid="tp-iepe-presentation">
      <div style={{ minHeight: '450px', padding: '1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <SlideContent slide={slides[currentSlide]} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1.5rem', borderTop: `1px solid ${theme.primary}20`, background: 'white' }}>
        <button onClick={prevSlide} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', border: `1px solid ${theme.primary}`, borderRadius: '0.5rem', background: 'white', cursor: 'pointer', color: theme.text, fontWeight: '500' }} data-testid="prev-slide-btn">
          <ChevronLeft size={18} /><span>Précédent</span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={() => setIsPlaying(!isPlaying)} style={{ width: '40px', height: '40px', borderRadius: '50%', border: 'none', background: theme.primary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }} data-testid="play-pause-btn">
            {isPlaying ? '⏸' : <Play size={16} />}
          </button>
          <span style={{ fontWeight: 'bold', color: theme.primary, fontSize: '0.95rem' }}>{currentSlide + 1} / {slides.length}</span>
        </div>
        <button onClick={nextSlide} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', border: 'none', borderRadius: '0.5rem', background: theme.primary, cursor: 'pointer', color: 'white', fontWeight: '500' }} data-testid="next-slide-btn">
          <span>Suivant</span><ChevronRight size={18} />
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', padding: '0.75rem', background: 'white', flexWrap: 'wrap' }}>
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setCurrentSlide(idx)} style={{ width: '8px', height: '8px', borderRadius: '50%', border: 'none', background: idx === currentSlide ? theme.primary : theme.secondary, cursor: 'pointer', opacity: idx === currentSlide ? 1 : 0.4 }} data-testid={`slide-dot-${idx}`} />
        ))}
      </div>
    </div>
  );
};

export default TPIEPEPresentation;
